import type { AuditCategory, AuditPanelState, AuditTabType } from '@/components/SiteAudit/types';
import { create } from 'zustand';

interface SiteAuditStore {
  panelState: AuditPanelState;
  activeTab: AuditTabType;
  seoCategories: AuditCategory[];
  performanceCategories: AuditCategory[];
  auditTime: Date | null;
  seoScore: number;
  performanceScore: number;
  totalScore: number;
  isGetSeoReportFinished: boolean;
  hasSeoReportResult: boolean;
  reportProjectId: string;
  isFetchingSeoReport: boolean;
  setPanelState: (panelState: AuditPanelState) => void;
  setActiveTab: (activeTab: AuditTabType) => void;
  setIsGetSeoReportFinished: (isFinished: boolean) => void;
  setHasSeoReportResult: (hasResult: boolean) => void;
  setSeoReportRequestState: (payload: { projectId: string; isFetching: boolean; isFinished: boolean }) => void;
  setAuditResult: (payload: {
    projectId: string;
    seoCategories: AuditCategory[];
    performanceCategories: AuditCategory[];
    auditTime: Date | null;
    seoScore: number;
    performanceScore: number;
    totalScore: number;
  }) => void;
  setNoSeoReportResult: (projectId: string) => void;
  clearAuditResult: () => void;
  reset: () => void;
}

const initialState = {
  panelState: 'idle' as AuditPanelState,
  activeTab: 'seo' as AuditTabType,
  seoCategories: [] as AuditCategory[],
  performanceCategories: [] as AuditCategory[],
  auditTime: null as Date | null,
  seoScore: 0,
  performanceScore: 0,
  totalScore: 0,
  isGetSeoReportFinished: false,
  hasSeoReportResult: false,
  reportProjectId: '',
  isFetchingSeoReport: false,
};

export const useSiteAuditStore = create<SiteAuditStore>(set => ({
  ...initialState,
  setPanelState: panelState => set({ panelState }),
  setActiveTab: activeTab => set({ activeTab }),
  setIsGetSeoReportFinished: isFinished => set({ isGetSeoReportFinished: isFinished }),
  setHasSeoReportResult: hasResult => set({ hasSeoReportResult: hasResult }),
  setSeoReportRequestState: ({ projectId, isFetching, isFinished }) => set({
    reportProjectId: projectId,
    isFetchingSeoReport: isFetching,
    isGetSeoReportFinished: isFinished,
  }),
  setAuditResult: ({
    projectId,
    seoCategories,
    performanceCategories,
    auditTime,
    seoScore,
    performanceScore,
    totalScore,
  }) => set({
    reportProjectId: projectId,
    panelState: 'completed',
    seoCategories,
    performanceCategories,
    auditTime,
    seoScore,
    performanceScore,
    totalScore,
    hasSeoReportResult: true,
    isFetchingSeoReport: false,
    isGetSeoReportFinished: true,
  }),
  setNoSeoReportResult: projectId => set({
    reportProjectId: projectId,
    panelState: 'idle',
    seoCategories: [],
    performanceCategories: [],
    auditTime: null,
    seoScore: 0,
    performanceScore: 0,
    totalScore: 0,
    hasSeoReportResult: false,
    isFetchingSeoReport: false,
    isGetSeoReportFinished: true,
  }),
  clearAuditResult: () => set({
    panelState: 'idle',
    seoCategories: [],
    performanceCategories: [],
    auditTime: null,
    seoScore: 0,
    performanceScore: 0,
    totalScore: 0,
    hasSeoReportResult: false,
    isFetchingSeoReport: false,
  }),
  reset: () => set(initialState),
}));
